import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";

import { BetslipSportsbookRemoveLegClick, UI__BETSLIP_SBK_REMOVE_LEG_CLICK } from "@ppb/tbd-store/actions/betslip";
import { BettingSportsbookRemoveLegAction, BETTING__SBK_REMOVE_LEG_ACTION } from "@ppb/tbd-store/actions/betting";
import { formatOdds } from "@ppb/formatters";
import { getSportsbookBettingLegs } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../../store/state/sportsbook-betting/betting-resolver";
import { createSportsbookBettingRunnerSelector } from "../../../store/state/sportsbook-betting/sportsbook-betting-selectors";
import { createOddsMovementSelector } from "../../../store/state/betslip-module/betslip-odds-movement-selectors";
import { BetslipApplicationState } from "../../../store/state/BetslipApplicationState";
import {
  getBetslipGroup,
  getBetslipPlaceStatus,
  getSportsbookOddsMovement,
  getIsBetslipLoaded,
} from "../../../store/state/betslip-module/betslip-module-selectors";

import { getSelectionTypeIcon } from "../../../helpers/selection-type";
import { toOdds } from "../../../formatters/odds-adapter";
import { i18n } from "../../../helpers/i18n";
import { buildSelection } from "../../connected-sportsbook-betslip-mapper";
import { ContainerProps, DispatchProps, StateProps } from "../props";

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const getOddsMovement = createOddsMovementSelector();
  const getSportsbookBettingRunner = createSportsbookBettingRunnerSelector();

  return (appState, { id, localization, capabilities, preferences }): StateProps => {
    if (!getIsBetslipLoaded(appState)) {
      return {};
    }

    const group = getBetslipGroup(appState);
    const resolvers = getBettingResolvers(group);
    const leg = getSportsbookBettingLegs(appState)[id];
    if (!leg) {
      return {};
    }

    const bettingRunnersMetadata = resolvers.getMetadata(appState);
    const oddsMovement = getOddsMovement(getSportsbookOddsMovement(appState));
    const { runners } = leg;
    const [legRunnerId] = runners;
    const metadata = bettingRunnersMetadata[legRunnerId];
    if (!metadata) {
      return {};
    }

    const bettingRunner = getSportsbookBettingRunner(appState.betting.sportsbookBetting.runners, legRunnerId);
    const { title, subtitle, icon, silkFallbackType, meetingCountry, racingSport, trap } = buildSelection(
      metadata,
      localization,
      bettingRunner,
    );
    const placeStatus = getBetslipPlaceStatus(appState);
    const isPlacing = placeStatus === "INPROGRESS";

    let odd;
    if (!leg.isBoosted) {
      odd =
        leg.odds && leg.displayOdds
          ? formatOdds(toOdds(leg.displayOdds), preferences.oddsDisplay)
          : i18n({ key: "I18N.BETSLIP.STARTING_PRICE" });
    }

    return {
      id: leg.id,
      urn: metadata.runnerUrn,
      title,
      subtitle,
      icon,
      silkFallbackType,
      racingSport,
      trap,
      meetingCountry,
      isPlacing,
      oddsMovement: !leg.isBoosted && leg.odds ? oddsMovement[leg.id]?.movement : undefined,
      odd,
      is90Min: metadata.is90Min,
      selectionTypeIcon: capabilities.selectionTypeIcon.enabled
        ? getSelectionTypeIcon(metadata.marketType, metadata.isSuperSub)
        : undefined,
      isTrapIconEnabled: capabilities.trapIcon.enabled,
    };
  };
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (dispatch: Dispatch) => ({
  dispatchSelectionRemove: (id: string, urn: string) => {
    dispatch<BetslipSportsbookRemoveLegClick>({
      type: UI__BETSLIP_SBK_REMOVE_LEG_CLICK,
      payload: {
        legId: id,
        runnerUrn: urn,
      },
    });
    dispatch<BettingSportsbookRemoveLegAction>({
      type: BETTING__SBK_REMOVE_LEG_ACTION,
      payload: { legId: id },
    });
  },
});
